import { defineStore } from 'pinia'
import { computed, ComputedRef, Ref, ref } from 'vue'
import currencyJs from 'currency.js'
import { TaxLine } from '@/components/PaymentRequest/types'
import { useCalculationsStore } from '@/components/PaymentRequest/store/useCalculationsStore'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'

export const useTaxStore = defineStore('payment-request-tax', () => {
  const paymentRequestStore = usePaymentRequestStore()
  const calculationsStore = useCalculationsStore()

  const taxLines: Ref<TaxLine[]> = ref([])

  /**
   * Add tax lines to the store
   */
  const addTaxLine = (incomingTaxLine: TaxLine) => {
    if (!taxLines.value.map((line) => line.terminology).includes(incomingTaxLine.terminology)) {
      taxLines.value.push(incomingTaxLine)
    }
  }

  /**
   * Patch tax lines in the store
   */
  const patchTaxLine = (incomingTaxLine: TaxLine) => {
    taxLines.value.forEach((taxLine) => {
      if (taxLine.terminology === incomingTaxLine.terminology) {
        // eslint-disable-next-line no-param-reassign
        taxLine.rate = incomingTaxLine.rate
      }
    })
  }

  /**
   * Merge tax lines which are added to this store
   * with the VAT provided from the paymentRequestStore
   */
  const allTaxLines: ComputedRef<
    { rate: number; terminology: string; ratePercentage: string; amountCharged: string }[]
  > = computed(() => {
    if (!paymentRequestStore.hasFreelancerVat) {
      return []
    }
    const nonZeroTaxLines = [
      ...taxLines.value,
      {
        rate: paymentRequestStore.vatRate,
        terminology: paymentRequestStore.vatTerminology,
      },
    ].filter((taxLine) => taxLine.rate > 0)

    return nonZeroTaxLines.map((taxLine) => ({
      rate: taxLine.rate,
      terminology: taxLine.terminology,
      ratePercentage: currencyJs(taxLine.rate * 100, paymentRequestStore.currencyConfig).format(),
      amountCharged: currencyJs(calculationsStore.earningsBeforeTax, paymentRequestStore.currencyConfig)
        .multiply(taxLine.rate)
        .format(),
    }))
  })

  return {
    allTaxLines,
    taxLines,
    addTaxLine,
    patchTaxLine,
  }
})
